import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { 
    Box, Typography, TextField, MenuItem, Paper, Grid, Button, 
    Chip, Pagination, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Avatar, Tooltip, IconButton 
} from "@mui/material";
import { 
    Plus, Search, Filter, Calendar, DollarSign, PieChart, 
    ChevronRight, Wallet, CheckCircle2, AlertCircle, FileText 
} from "lucide-react";
import { agregarAdelanto } from "../../features/registroAdelantosSlice";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  aqua: "#00C0CA",
  teal: "#008B9C",
  azur: "#28708B",
  dark: "#22404D",
  lime: "#8FC440",
  yellow: "#E3E24F",
  pearl: "#EEF3F4",
  textGray: "#64748B"
};

const ListaAdelantos = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [adelantos, setAdelantos] = useState([]);
    const [adelantosFiltrados, setAdelantosFiltrados] = useState([]);
    const [filtros, setFiltros] = useState({
        programa: '',
        centroCosto: '',
        estado: '',
        ordenarPorFecha: 'desc'
    });
    const [paginaActual, setPaginaActual] = useState(1);
    const itemsPorPagina = 10;

    useEffect(() => {
        const obtenerAdelantos = async () => {
            const response = await fetch(`${apiUrl}/Adelanto`, {
                headers: {
                    'Content-Type': 'application/json',
                    'apiKey': localStorage.getItem("apiKey")
                }
            });
            if (response.ok) {
                const data = await response.json();
                setAdelantos(data);
                dispatch(agregarAdelanto(data));
                setAdelantosFiltrados(data);
            }
        };
        obtenerAdelantos();
    }, [dispatch]);

    useEffect(() => {
        let filtrados = [...adelantos];
        if (filtros.programa) {
            filtrados = filtrados.filter(a => a.programa.nombre.toLowerCase().includes(filtros.programa.toLowerCase()));
        }
        if (filtros.centroCosto) {
            filtrados = filtrados.filter(a => a.centroCosto.toLowerCase().includes(filtros.centroCosto.toLowerCase()));
        }
        if (filtros.estado) {
            filtrados = filtrados.filter(a => a.estado.toLowerCase().includes(filtros.estado.toLowerCase()));
        }
        filtrados.sort((a, b) => {
            const dateA = new Date(a.fecha);
            const dateB = new Date(b.fecha);
            return filtros.ordenarPorFecha === 'asc' ? dateA - dateB : dateB - dateA;
        });
        setAdelantosFiltrados(filtrados);
    }, [filtros, adelantos]);

    const handleFiltroCambio = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
        setPaginaActual(1);
    };

    const totalPages = Math.ceil(adelantosFiltrados.length / itemsPorPagina);
    const adelantosPagidados = adelantosFiltrados.slice((paginaActual - 1) * itemsPorPagina, paginaActual * itemsPorPagina);

    return (
        <Box sx={{ p: { xs: 2, md: 6 }, pt: { xs: 10, md: 14 }, backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
            
            {/* HEADER */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: PALETTE.ice, color: PALETTE.azur, width: 50, height: 50 }}>
                        <Wallet size={24} />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: PALETTE.dark }}>Adelantos</Typography>
                        <Typography variant="body2" sx={{ color: PALETTE.textGray, fontWeight: 500 }}>Control financiero y rendiciones</Typography>
                    </Box>
                </Box>

                {localStorage.getItem("rol") === "coordinador" && (
                    <Button 
                        variant="contained" 
                        startIcon={<Plus size={20} />}
                        onClick={() => navigate("/FormularioAdelanto")}
                        sx={{ 
                            borderRadius: '14px', px: 3, py: 1.2, textTransform: 'none', fontWeight: 800,
                            bgcolor: PALETTE.dark, '&:hover': { bgcolor: PALETTE.teal }
                        }}
                    >
                        Nuevo adelanto
                    </Button>
                )}
            </Box>

            {/* FILTROS */}
            <Paper elevation={0} sx={{ p: 2.5, mb: 4, borderRadius: '24px', backgroundColor: PALETTE.pearl, border: `1px solid ${PALETTE.sky}` }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField fullWidth size="small" name="programa" placeholder="Programa" value={filtros.programa} onChange={handleFiltroCambio}
                            InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' }, startAdornment: <PieChart size={16} style={{marginRight: 8, color: PALETTE.teal}} /> }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField fullWidth size="small" name="centroCosto" placeholder="Centro de Costo" value={filtros.centroCosto} onChange={handleFiltroCambio}
                            InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField select fullWidth size="small" name="estado" value={filtros.estado} onChange={handleFiltroCambio}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}>
                            <MenuItem value="">Todos los estados</MenuItem>
                            <MenuItem value="Aprobado">Aprobado</MenuItem>
                            <MenuItem value="Pendiente">Pendiente</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField select fullWidth size="small" name="ordenarPorFecha" value={filtros.ordenarPorFecha} onChange={handleFiltroCambio}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}>
                            <MenuItem value="desc">Más recientes</MenuItem>
                            <MenuItem value="asc">Más antiguos</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            {/* TABLA */}
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '24px', border: '1px solid #eee' }}>
                <Table sx={{ minWidth: 1100 }}>
                    <TableHead sx={{ bgcolor: '#fbfcfc' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>PROGRAMA / NOMBRE</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>MOTIVO</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>FECHA / CC</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>IMPORTE</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>ESTADO ADELANTO</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>RENDICIÓN</TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {adelantosPagidados.map(a => (
                            <TableRow key={a.id} hover sx={{ transition: '0.2s' }}>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 800, color: PALETTE.dark }}>{a.programa.nombre}</Typography>
                                    <Typography variant="caption" sx={{ color: PALETTE.textGray }}>{a.nombre}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Tooltip title={a.motivo}>
                                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {a.motivo}
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <Calendar size={14} color={PALETTE.teal} />
                                        <Typography variant="caption" fontWeight={600}>{a.fecha.split("T")[0]}</Typography>
                                    </Box>
                                    <Chip label={`CC: ${a.centroCosto}`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: PALETTE.pearl }} />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 900, color: PALETTE.dark }}>
                                        {a.moneda === "USD" ? "U$S" : "$"} {Number(a.importe).toLocaleString()}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label={a.estado} 
                                        size="small"
                                        sx={{ 
                                            fontWeight: 800, borderRadius: '8px',
                                            bgcolor: a.estado === "Aprobado" ? `${PALETTE.lime}15` : `${PALETTE.textGray}15`,
                                            color: a.estado === "Aprobado" ? PALETTE.lime : PALETTE.textGray,
                                            border: `1px solid ${a.estado === "Aprobado" ? PALETTE.lime : PALETTE.textGray}`
                                        }} 
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label={a?.laRendicion?.rendicioUsuario && a?.laRendicion?.rendicionFinanzas ? "Terminada" : "Pendiente"}
                                        size="small"
                                        variant="outlined"
                                        icon={a?.laRendicion?.rendicioUsuario && a?.laRendicion?.rendicionFinanzas ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
                                        sx={{ 
                                            fontWeight: 800, borderRadius: '8px',
                                            borderColor: a?.laRendicion?.rendicioUsuario && a?.laRendicion?.rendicionFinanzas ? PALETTE.lime : PALETTE.yellow,
                                            color: a?.laRendicion?.rendicionFinanzas ? PALETTE.lime : "#9A8D00"
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton 
                                        onClick={() => navigate('/adelanto/' + a.id)}
                                        sx={{ color: PALETTE.dark, '&:hover': { bgcolor: PALETTE.ice, color: PALETTE.teal } }}
                                    >
                                        <ChevronRight size={22} />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {adelantosPagidados.length === 0 && (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">No se encontraron registros financieros.</Typography>
                    </Box>
                )}
            </TableContainer>

            {/* PAGINACIÓN */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Pagination 
                    count={totalPages} page={paginaActual} onChange={(e, v) => setPaginaActual(v)}
                    sx={{ '& .MuiPaginationItem-root': { borderRadius: '12px', fontWeight: 700 }, '& .Mui-selected': { bgcolor: `${PALETTE.dark} !important`, color: 'white' } }}
                />
            </Box>
        </Box>
    );
};

export default ListaAdelantos;